import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Field, inputCls } from "./ui";

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setForm(r.data);
      setAnnouncements(
        (r.data.announcements || []).map((a) =>
          typeof a === "string" ? { en: a, my: "" } : { en: a.en || "", my: a.my || "" }
        )
      );
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        ...form,
        announcements: announcements
          .map((a) => ({ en: (a.en || "").trim(), my: (a.my || "").trim() }))
          .filter((a) => a.en || a.my),
      });
      toast.success("Settings saved — live on the site now.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <div className="h-64 rounded-2xl bg-white animate-pulse" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAnn = (i, k, v) => setAnnouncements((arr) => arr.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="admin-settings">
      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6 space-y-4">
        <p className="font-display font-bold text-ink">Branding</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Brand name"><input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className={inputCls} data-testid="settings-brand-input" /></Field>
          <Field label="Contact email"><input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} data-testid="settings-email-input" /></Field>
          <Field label="Contact phone (shown in footer)"><input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} placeholder="347-445-6381" data-testid="settings-phone-input" /></Field>
          <Field label="Facebook URL"><input value={form.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} className={inputCls} placeholder="https://facebook.com/…" data-testid="settings-facebook-input" /></Field>
          <Field label="Instagram URL"><input value={form.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className={inputCls} placeholder="https://instagram.com/…" data-testid="settings-instagram-input" /></Field>
          <Field label="Tagline"><input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className={inputCls} data-testid="settings-tagline-input" /></Field>
          <Field label="Sub-tagline"><input value={form.sub_tagline} onChange={(e) => set("sub_tagline", e.target.value)} className={inputCls} data-testid="settings-subtagline-input" /></Field>
        </div>
        <Field label="Hero overline"><input value={form.hero_overline} onChange={(e) => set("hero_overline", e.target.value)} className={inputCls} data-testid="settings-overline-input" /></Field>
        <Field label="Footer note"><input value={form.footer_note} onChange={(e) => set("footer_note", e.target.value)} className={inputCls} data-testid="settings-footer-input" /></Field>
      </div>

      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6 space-y-4">
        <p className="font-display font-bold text-ink">Community announcements</p>
        <p className="text-xs text-stone2">Write each announcement in English and Burmese — visitors see the language they chose. Burmese is optional (English shows as fallback).</p>
        <div className="space-y-4" data-testid="settings-announcements-list">
          {announcements.map((a, i) => (
            <div key={i} className="border border-sand rounded-xl p-4 space-y-3 relative" data-testid={`settings-announcement-${i}`}>
              <button
                type="button"
                onClick={() => setAnnouncements((arr) => arr.filter((_, idx) => idx !== i))}
                className="absolute top-3 right-3 p-1.5 text-stone2 hover:text-ruby rounded-lg hover:bg-sand"
                data-testid={`settings-announcement-remove-${i}`}
                aria-label="Remove announcement"
              >
                <X className="w-4 h-4" />
              </button>
              <Field label={`Announcement ${i + 1} — English`}>
                <textarea rows={2} value={a.en} onChange={(e) => setAnn(i, "en", e.target.value)} className={inputCls} data-testid={`settings-announcement-en-${i}`} />
              </Field>
              <Field label="မြန်မာ (Burmese)">
                <textarea rows={2} value={a.my} onChange={(e) => setAnn(i, "my", e.target.value)} className={inputCls} style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }} data-testid={`settings-announcement-my-${i}`} />
              </Field>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setAnnouncements((arr) => [...arr, { en: "", my: "" }])}
          className="inline-flex items-center gap-2 text-sm font-semibold text-ruby border border-ruby/30 hover:bg-ruby-light px-4 py-2.5 rounded-xl transition-colors"
          data-testid="settings-announcement-add"
        >
          <Plus className="w-4 h-4" /> Add announcement
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6 space-y-4">
        <p className="font-display font-bold text-ink">Notifications & owners</p>
        <Field label="Admin notification email — new listings & ad requests land here">
          <input value={form.notification_email || ""} onChange={(e) => set("notification_email", e.target.value)} className={inputCls} placeholder="you@burmesehub.com" data-testid="settings-notify-email-input" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
          <input type="checkbox" checked={!!form.owner_auto_publish} onChange={(e) => set("owner_auto_publish", e.target.checked)} className="w-4 h-4 accent-[#8B1A24]" data-testid="settings-auto-publish-check" />
          Owner edits publish automatically (off = edits need admin re-approval)
        </label>
      </div>

      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6 space-y-4">
        <p className="font-display font-bold text-ink">Policy pages</p>
        <p className="text-xs text-stone2">Blank line between paragraphs. Start a line with ## for a heading.</p>
        <Field label="Privacy Policy"><textarea rows={5} value={form.privacy_text || ""} onChange={(e) => set("privacy_text", e.target.value)} className={inputCls} data-testid="settings-privacy-input" /></Field>
        <Field label="Terms of Use"><textarea rows={5} value={form.terms_text || ""} onChange={(e) => set("terms_text", e.target.value)} className={inputCls} data-testid="settings-terms-input" /></Field>
        <Field label="Community Guidelines"><textarea rows={5} value={form.guidelines_text || ""} onChange={(e) => set("guidelines_text", e.target.value)} className={inputCls} data-testid="settings-guidelines-input" /></Field>
        <Field label="About page"><textarea rows={5} value={form.about_text || ""} onChange={(e) => set("about_text", e.target.value)} className={inputCls} data-testid="settings-about-input" /></Field>
      </div>

      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6 space-y-4">
        <p className="font-display font-bold text-ink">SEO</p>
        <Field label="Meta title"><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} data-testid="settings-seo-title-input" /></Field>
        <Field label="Meta description"><textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={inputCls} data-testid="settings-seo-desc-input" /></Field>
      </div>

      <button type="submit" disabled={busy} className="bg-ruby hover:bg-ruby-dark disabled:opacity-60 text-cream font-bold px-10 py-3.5 rounded-xl" data-testid="settings-save-btn">
        {busy ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
}
